/**
 * Mapping tab: Pax8 companies ↔ customers and Pax8 products ↔ Books items (#1111). A person confirms every mapping;
 * a name or SKU match only suggests, and nothing is reconciled until it is confirmed. Rejecting a suggestion means it
 * is not offered again. Companies pick a Conduit customer through the shared customer search (the BooksPicker way:
 * the suggestion first, then search); products pick a Books item (Ewee Inc) in a dialog like the profit Data
 * quality tab's LinkDialog.
 */
import { Button, Chip, Dialog, EmptyState, Section, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { describeError, unwrap } from '../../lib/api/client'
import {
  type BooksItemOut,
  type BooksItemsOut,
  type CompanyMappingOut,
  type LicenceMappingIn,
  type LinkState,
  listBooksItemsApiV1BillingBooksItemsGet,
  type ProductMappingOut,
  setLicenceMappingApiV1LicencesMappingPost,
} from '../../lib/api/generated'
import { CustomerPicker } from '../profit/filter-bar'
import { timeLabel } from '../profit/format'
import { QueryView } from '../profit/states'
import { ExtLink, FilterChips, licenceKeys, mappingQuery, pax8Url, SearchBox } from './common'

/** Ewee Inc bills M365 (Clayton, 2026-10-29), so product links default to its Books org. */
export const EWEE_BOOKS_ORG = '847122427'

type View = 'decide' | 'confirmed' | 'rejected' | 'all'
const needsDecision = (state: LinkState | null | undefined) => !state || state === 'suggested'
const VIEW_TEST: Record<View, (s: LinkState | null | undefined) => boolean> = {
  decide: needsDecision,
  confirmed: (s) => s === 'confirmed',
  rejected: (s) => s === 'rejected',
  all: () => true,
}

function useSetMapping() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: LicenceMappingIn) => unwrap(setLicenceMappingApiV1LicencesMappingPost({ body })),
    onSuccess: async (_out, body) => {
      toast.success(body.state === 'confirmed' ? 'Mapping confirmed' : 'Suggestion rejected')
      // A confirmed map changes every reconciled line, not only this list.
      await queryClient.invalidateQueries({ queryKey: licenceKeys.all })
    },
    onError: (error) => toast.error(`Not saved: ${describeError(error)}`),
  })
}

function StateChip({ state, auto = false }: { state: LinkState | null | undefined; auto?: boolean }) {
  if (state === 'confirmed') return <Chip tone="success">{auto ? 'auto-matched' : 'confirmed'}</Chip>
  if (state === 'rejected') return <Chip tone="neutral">rejected</Chip>
  if (state === 'suggested') return <Chip tone="warning">suggested</Chip>
  return <Chip tone="danger">no match</Chip>
}

function Who({ by, at }: { by?: { name: string } | null; at?: string | null }) {
  if (!by && !at) return null
  return <span className="block text-xs text-subtle">{[by?.name, at ? timeLabel(at) : null].filter(Boolean).join(' · ')}</span>
}

// --- companies ---------------------------------------------------------------------------------------------------------

function CompanyDialog({ row, onClose }: { row: CompanyMappingOut | null; onClose: () => void }) {
  const save = useSetMapping()
  const [picked, setPicked] = React.useState<{ id: string; label: string } | null>(null)
  const suggestion =
    row?.state === 'suggested' && row.customer_id ? { id: row.customer_id, label: row.customer_name ?? 'Suggested customer' } : null
  const confirmLink = (customer: { id: string }) => {
    if (!row) return
    save.mutate(
      { kind: 'company', state: 'confirmed', pax8_company_id: row.pax8_company_id, customer_id: customer.id },
      { onSuccess: onClose },
    )
  }
  return (
    <Dialog
      open={Boolean(row)}
      onOpenChange={(open) => !open && onClose()}
      title="Map Pax8 company to a customer"
      description={
        row ? `${row.pax8_name} · ${row.active_subscriptions} active subscription${row.active_subscriptions === 1 ? '' : 's'}` : undefined
      }
    >
      <div className="space-y-3">
        {suggestion && !picked ? (
          <div className="rounded-md border border-border p-2.5 text-sm">
            <p className="text-xs text-muted-foreground">
              Suggested by name{row?.confidence ? ` (match ${Number(row.confidence).toFixed(2)})` : ''}
            </p>
            <p className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{suggestion.label}</span>
              <Button size="sm" disabled={save.isPending} onClick={() => confirmLink(suggestion)}>
                Use this one
              </Button>
            </p>
          </div>
        ) : null}
        {picked ? (
          <p className="text-sm">
            Map to <strong>{picked.label}</strong>{' '}
            <Button size="sm" variant="link" onClick={() => setPicked(null)}>
              change
            </Button>
          </p>
        ) : (
          <CustomerPicker label="Customer" onPick={setPicked} autoFocus={!suggestion} />
        )}
        {save.error ? <p className="text-sm text-destructive">Not mapped: {describeError(save.error)}</p> : null}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!picked || save.isPending} onClick={() => picked && confirmLink(picked)}>
            Confirm mapping
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function CompanyRow({ row, onChoose }: { row: CompanyMappingOut; onChoose: () => void }) {
  const save = useSetMapping()
  const target = row.customer_name ?? row.books_customer_name
  // Confirmed by the nightly name match, not a person (#1202): a person can still reject it.
  const auto = row.state === 'confirmed' && row.method === 'name' && !row.confirmed_by
  return (
    <li
      data-testid="company-mapping"
      data-company={row.pax8_company_id}
      className="grid gap-2 border-b border-border px-3 py-2.5 last:border-0 md:grid-cols-[minmax(12rem,1fr)_minmax(12rem,1fr)_17rem] md:items-center"
    >
      <div className="min-w-0">
        <ExtLink href={pax8Url(row.pax8_company_id)} className="font-medium">
          {row.pax8_name}
        </ExtLink>
        <span className="block text-xs text-muted-foreground">
          {row.active_subscriptions} active subscription{row.active_subscriptions === 1 ? '' : 's'}
        </span>
      </div>
      <div className="min-w-0 text-sm">
        <span className="flex flex-wrap items-center gap-1.5">
          <StateChip state={row.state} auto={auto} />
          {target ? <span className={row.state === 'rejected' ? 'text-muted-foreground line-through' : ''}>{target}</span> : null}
          {(row.state === 'suggested' || auto) && row.confidence ? (
            <span className="text-xs text-muted-foreground">name match {Number(row.confidence).toFixed(2)}</span>
          ) : null}
        </span>
        {row.books_customer_name && row.customer_name && row.books_customer_name !== row.customer_name ? (
          <span className="block text-xs text-muted-foreground">Books: {row.books_customer_name}</span>
        ) : null}
        <Who by={row.confirmed_by} at={row.confirmed_at} />
      </div>
      <div className="flex flex-wrap gap-1.5 md:justify-end">
        {row.state === 'suggested' && row.customer_id ? (
          <Button
            size="sm"
            variant="primary"
            disabled={save.isPending}
            aria-label={`Confirm ${row.pax8_name} is ${row.customer_name ?? 'the suggested customer'}`}
            onClick={() =>
              save.mutate({
                kind: 'company',
                state: 'confirmed',
                pax8_company_id: row.pax8_company_id,
                customer_id: row.customer_id ?? null,
              })
            }
          >
            Confirm
          </Button>
        ) : null}
        <Button size="sm" onClick={onChoose} aria-label={`Choose a customer for ${row.pax8_name}`}>
          {row.state === 'confirmed' ? 'Change' : 'Choose…'}
        </Button>
        {row.state === 'suggested' || auto ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={save.isPending}
            aria-label={`Reject the ${auto ? 'automatic match' : 'suggestion'} for ${row.pax8_name}`}
            onClick={() =>
              save.mutate({
                kind: 'company',
                state: 'rejected',
                pax8_company_id: row.pax8_company_id,
                customer_id: row.customer_id ?? null,
              })
            }
          >
            Reject
          </Button>
        ) : null}
      </div>
    </li>
  )
}

// --- products ----------------------------------------------------------------------------------------------------------

const words = (s: string) =>
  new Set(
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .filter((w) => w.length > 1 && !['microsoft', 'the', 'and', 'for', 'nce', 'monthly', 'annual', 'yearly', 'commit'].includes(w)),
  )

/** Rank Books items for a Pax8 product: the SKU carried in the item wins, then shared name words (Jaccard). */
export function rankItems(product: Pick<ProductMappingOut, 'product_name' | 'sku'>, items: BooksItemOut[], limit = 6): BooksItemOut[] {
  const sku = product.sku?.toLowerCase()
  const pw = words(product.product_name)
  const score = (item: BooksItemOut) => {
    if (sku && `${item.sku} ${item.name}`.toLowerCase().includes(sku)) return 2
    const iw = words(item.name)
    const shared = [...pw].filter((w) => iw.has(w)).length
    return shared === 0 ? 0 : shared / new Set([...pw, ...iw]).size
  }
  return items
    .map((item) => ({ item, s: score(item) }))
    .filter((x) => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map((x) => x.item)
}

function ProductDialog({ row, onClose }: { row: ProductMappingOut | null; onClose: () => void }) {
  const save = useSetMapping()
  const org = row?.books_org_id ?? EWEE_BOOKS_ORG
  const items = useQuery({
    queryKey: ['billing', 'books-items', org],
    queryFn: () => unwrap<BooksItemsOut>(listBooksItemsApiV1BillingBooksItemsGet({ query: { books_org_id: org } })),
    enabled: Boolean(row),
    staleTime: 5 * 60_000,
  })
  const [term, setTerm] = React.useState('')
  const list = items.data?.state === 'ok' ? (items.data.items ?? []) : []
  const t = term.trim().toLowerCase()
  const shown = row ? (t ? list.filter((i) => `${i.name} ${i.sku}`.toLowerCase().includes(t)).slice(0, 12) : rankItems(row, list)) : []
  const pick = (item: BooksItemOut) => {
    if (!row) return
    save.mutate(
      { kind: 'product', state: 'confirmed', pax8_product_id: row.pax8_product_id, books_org_id: org, books_item_id: item.item_id },
      { onSuccess: onClose },
    )
  }
  return (
    <Dialog
      open={Boolean(row)}
      onOpenChange={(open) => !open && onClose()}
      title="Link Pax8 product to a Books item"
      description={row ? `${row.product_name}${row.sku ? ` (${row.sku})` : ''} · Ewee Inc Books` : undefined}
    >
      <div className="space-y-3">
        <SearchBox value={term} onChange={setTerm} label="Search Books items" />
        {items.isPending ? <p className="text-sm text-muted-foreground">Loading Books items…</p> : null}
        {items.isError || (items.data && items.data.state !== 'ok') ? (
          <p className="text-sm text-warning">
            Books items are {items.data?.state === 'scope_missing' ? 'not readable with the current Books grant' : 'unreachable'}; that is
            not the same as no match.
          </p>
        ) : null}
        {items.data?.state === 'ok' ? (
          <>
            <p className="text-xs text-muted-foreground">{t ? 'Matches' : 'Suggestions (SKU first, then name)'}</p>
            {shown.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t ? 'No Books item matches that search.' : 'No likely item; search above.'}</p>
            ) : (
              <ul aria-label="Books items" className="divide-y divide-border rounded-md border border-border">
                {shown.map((item) => (
                  <li key={item.item_id} className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="block break-words">{item.name}</span>
                      <span className="block font-mono text-xs text-muted-foreground">{item.sku || 'no SKU'}</span>
                    </span>
                    <Button size="sm" disabled={save.isPending} onClick={() => pick(item)} aria-label={`Use ${item.name}`}>
                      Use this one
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
        {save.error ? <p className="text-sm text-destructive">Not linked: {describeError(save.error)}</p> : null}
        <div className="flex justify-end pt-1">
          <Button onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </Dialog>
  )
}

function ProductRow({ row, onChoose }: { row: ProductMappingOut; onChoose: () => void }) {
  const save = useSetMapping()
  const body = (state: 'confirmed' | 'rejected'): LicenceMappingIn => ({
    kind: 'product',
    state,
    pax8_product_id: row.pax8_product_id,
    books_org_id: row.books_org_id ?? EWEE_BOOKS_ORG,
    books_item_id: row.books_item_id ?? null,
  })
  return (
    <li
      data-testid="product-mapping"
      data-product={row.pax8_product_id}
      className="grid gap-2 border-b border-border px-3 py-2.5 last:border-0 md:grid-cols-[minmax(12rem,1fr)_minmax(12rem,1fr)_17rem] md:items-center"
    >
      <div className="min-w-0">
        <p className="font-medium">{row.product_name}</p>
        <p className="font-mono text-xs text-muted-foreground">{row.sku ?? 'no SKU'}</p>
      </div>
      <div className="min-w-0 text-sm">
        <span className="flex flex-wrap items-center gap-1.5">
          <StateChip state={row.state} />
          {row.books_item_name ? (
            <span className={row.state === 'rejected' ? 'text-muted-foreground line-through' : ''}>{row.books_item_name}</span>
          ) : null}
          {row.state === 'suggested' && row.method ? (
            <span className="text-xs text-muted-foreground">
              {row.method === 'sku' ? 'SKU match' : row.method === 'name_tier' ? 'name match' : 'picked'}
            </span>
          ) : null}
        </span>
        <Who by={row.confirmed_by} at={row.confirmed_at} />
      </div>
      <div className="flex flex-wrap gap-1.5 md:justify-end">
        {row.state === 'suggested' && row.books_item_id ? (
          <Button
            size="sm"
            variant="primary"
            disabled={save.isPending}
            onClick={() => save.mutate(body('confirmed'))}
            aria-label={`Confirm ${row.product_name} is ${row.books_item_name ?? 'the suggested item'}`}
          >
            Confirm
          </Button>
        ) : null}
        <Button size="sm" onClick={onChoose} aria-label={`Choose a Books item for ${row.product_name}`}>
          {row.state === 'confirmed' ? 'Change' : 'Choose…'}
        </Button>
        {row.state === 'suggested' ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={save.isPending}
            onClick={() => save.mutate(body('rejected'))}
            aria-label={`Reject the suggestion for ${row.product_name}`}
          >
            Reject
          </Button>
        ) : null}
      </div>
    </li>
  )
}

export function MappingTab() {
  const q = useQuery(mappingQuery())
  const [view, setView] = React.useState<View>('decide')
  const [search, setSearch] = React.useState('')
  const [company, setCompany] = React.useState<CompanyMappingOut | null>(null)
  const [product, setProduct] = React.useState<ProductMappingOut | null>(null)
  return (
    <QueryView query={q} what="Mappings">
      {(data) => {
        const allCompanies = data.companies ?? []
        const allProducts = data.products ?? []
        const t = search.trim().toLowerCase()
        const companies = allCompanies
          .filter(
            (c) =>
              VIEW_TEST[view](c.state) &&
              (!t || `${c.pax8_name} ${c.customer_name ?? ''} ${c.books_customer_name ?? ''}`.toLowerCase().includes(t)),
          )
          .sort((a, b) => (b.active_subscriptions ?? 0) - (a.active_subscriptions ?? 0) || a.pax8_name.localeCompare(b.pax8_name))
        const products = allProducts
          .filter(
            (p) =>
              VIEW_TEST[view](p.state) && (!t || `${p.product_name} ${p.sku ?? ''} ${p.books_item_name ?? ''}`.toLowerCase().includes(t)),
          )
          .sort((a, b) => a.product_name.localeCompare(b.product_name))
        const count = (test: (s: LinkState | null | undefined) => boolean) =>
          allCompanies.filter((c) => test(c.state)).length + allProducts.filter((p) => test(p.state)).length
        return (
          <div className="space-y-3.5">
            <p className="max-w-3xl text-sm text-muted-foreground">
              Nothing is compared until a person confirms its mapping. A name or SKU match only suggests; a rejected suggestion is not
              offered again.
            </p>
            <FilterChips
              label="Mapping state"
              value={view}
              onChange={setView}
              options={[
                { id: 'decide', label: 'Needs a decision', count: count(VIEW_TEST.decide) },
                { id: 'confirmed', label: 'Confirmed', count: count(VIEW_TEST.confirmed) },
                { id: 'rejected', label: 'Rejected', count: count(VIEW_TEST.rejected) },
                { id: 'all', label: 'All', count: count(VIEW_TEST.all) },
              ]}
            />
            <SearchBox value={search} onChange={setSearch} label="Search companies, customers, products" />
            <Section title="Companies ↔ customers" count={companies.length}>
              {companies.length === 0 ? (
                <EmptyState title={view === 'decide' ? 'Every Pax8 company has a decision.' : 'No company in this view.'} />
              ) : (
                <ul aria-label="Company mappings" className="rounded-lg border border-border bg-card">
                  {companies.map((c) => (
                    <CompanyRow key={c.pax8_company_id} row={c} onChoose={() => setCompany(c)} />
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Products ↔ Books items" count={products.length}>
              {products.length === 0 ? (
                <EmptyState title={view === 'decide' ? 'Every Pax8 product has a decision.' : 'No product in this view.'} />
              ) : (
                <ul aria-label="Product mappings" className="rounded-lg border border-border bg-card">
                  {products.map((p) => (
                    <ProductRow key={p.pax8_product_id} row={p} onChoose={() => setProduct(p)} />
                  ))}
                </ul>
              )}
            </Section>
            <CompanyDialog key={company?.pax8_company_id ?? 'none'} row={company} onClose={() => setCompany(null)} />
            <ProductDialog key={product?.pax8_product_id ?? 'none'} row={product} onClose={() => setProduct(null)} />
          </div>
        )
      }}
    </QueryView>
  )
}
